import { z } from 'zod'

/**
 * What an AI app's connection may do in one organization: test mode only,
 * test plus reading live data, or test plus live changes. Always narrowed
 * further by the person's role and the organization's MCP setting.
 */
export const MCP_ACCESS_LEVELS = ['test_only', 'live_read', 'live'] as const

export const mcpAccessLevelSchema = z.enum(MCP_ACCESS_LEVELS)

export type McpAccessLevel = z.infer<typeof mcpAccessLevelSchema>

export const PRISMA_MCP_ACCESS_LEVELS = ['TEST_ONLY', 'LIVE_READ', 'LIVE'] as const

export type PrismaMcpAccessLevel = (typeof PRISMA_MCP_ACCESS_LEVELS)[number]

export const MCP_ACCESS_LEVEL_FROM_PRISMA = {
  TEST_ONLY: 'test_only',
  LIVE_READ: 'live_read',
  LIVE: 'live',
} as const satisfies Record<PrismaMcpAccessLevel, McpAccessLevel>

export const MCP_ACCESS_LEVEL_TO_PRISMA = {
  test_only: 'TEST_ONLY',
  live_read: 'LIVE_READ',
  live: 'LIVE',
} as const satisfies Record<McpAccessLevel, PrismaMcpAccessLevel>

const ACCESS_ORDER: Record<McpAccessLevel, number> = { test_only: 1, live_read: 2, live: 3 }

/** Whether `level` allows at least `needed`. */
export function accessAtLeast(level: McpAccessLevel, needed: McpAccessLevel): boolean {
  return ACCESS_ORDER[level] >= ACCESS_ORDER[needed]
}

/** The lower of two access levels. */
export function minAccessLevel(a: McpAccessLevel, b: McpAccessLevel): McpAccessLevel {
  return ACCESS_ORDER[a] <= ACCESS_ORDER[b] ? a : b
}

/** The least access in `levels`, or null when nothing is offered. */
export function lowestAccessLevel(levels: readonly McpAccessLevel[]): McpAccessLevel | null {
  let lowest: McpAccessLevel | null = null
  for (const level of levels) {
    lowest = lowest === null ? level : minAccessLevel(lowest, level)
  }
  return lowest
}

/** How an access level is named on the connect page and Connected apps. */
export const MCP_ACCESS_LEVEL_LABELS = {
  test_only: 'Test only',
  live_read: 'Test + live read-only',
  live: 'Test + live',
} as const satisfies Record<McpAccessLevel, string>
